import { Injectable } from '@angular/core';
import { ChatMessage } from '../models/chat.models';

/**
 * Desktop notifications and the incoming-message ping.
 *
 * Deliberately small, per the brief: a badge, an optional browser notification, and a sound —
 * no settings screen. Whether a message makes a sound is the **server's** call: every
 * `message-new` frame carries a `sound` boolean that is already false for a recipient on Do Not
 * Disturb, so this service never has to reason about presence.
 */
@Injectable({ providedIn: 'root' })
export class ChatNotificationService {
  private audioContext: AudioContext | null = null;
  private permissionAsked = false;
  private permissionGestureBound = false;
  private unlockBound = false;

  constructor() {
    this.bindUnlock();
  }

  /** Notifications need a user gesture on some browsers; call this from an explicit click. */
  requestPermission(): void {
    if (this.permissionAsked || !this.supported) {
      return;
    }
    this.permissionAsked = true;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined);
    }
  }

  /**
   * Ask on the first interaction of the session, wherever in Remmi it happens.
   *
   * The prompt used to wait for the user to open the chat widget or land on `/chat`, which meant a
   * colleague who never opens chat — and is therefore exactly the person a notification is *for* —
   * could never be sent one: permission had never been asked, so every message was silent. Bound
   * to a gesture rather than fired on load because browsers refuse the request without one, and
   * Chrome permanently blocks an origin that asks too eagerly.
   */
  requestPermissionOnFirstGesture(): void {
    if (this.permissionAsked || this.permissionGestureBound || !this.supported || typeof window === 'undefined') {
      return;
    }
    if (Notification.permission !== 'default') {
      // Already granted or already denied — asking again does nothing in either case.
      return;
    }
    this.permissionGestureBound = true;
    const ask = () => {
      this.requestPermission();
      window.removeEventListener('pointerdown', ask);
      window.removeEventListener('keydown', ask);
    };
    window.addEventListener('pointerdown', ask, { once: true });
    window.addEventListener('keydown', ask, { once: true });
  }

  /**
   * Browsers refuse to start an AudioContext that no gesture has ever authorised, and the first
   * message of a session almost never arrives during a click. So the context is created and
   * resumed on the first interaction of any kind and then left open — otherwise the very ping
   * this service exists for is the one that gets swallowed.
   */
  private bindUnlock(): void {
    if (this.unlockBound || typeof window === 'undefined') {
      return;
    }
    this.unlockBound = true;
    const unlock = () => {
      this.context();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  get supported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  get granted(): boolean {
    return this.supported && Notification.permission === 'granted';
  }

  /**
   * Raise a desktop notification for an incoming message and, if the server allowed it, ping.
   * `onClick` opens the conversation — the brief's "clicking a notification opens the
   * conversation".
   */
  notify(message: ChatMessage, conversationLabel: string, sound: boolean, onClick: () => void): void {
    if (sound) {
      this.ping();
    }

    if (!this.granted || document.visibilityState === 'visible') {
      // A visible tab already shows the message arriving; a toast on top of it is noise.
      return;
    }

    try {
      const body = message.body || (message.attachments?.length ? `📎 ${message.attachments[0].name}` : 'Sent a message');
      const notification = new Notification(`${message.sender.name}${conversationLabel ? ` · ${conversationLabel}` : ''}`, {
        body: body.length > 140 ? `${body.slice(0, 137)}…` : body,
        icon: 'assets/img/logo/favicon.png',
        // Same tag per conversation, so a burst of messages replaces rather than stacks.
        tag: `remmi-chat-${message.conversation_id}`,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
        onClick();
      };
    } catch {
      // Some browsers throw when constructing notifications in odd contexts — never break the send path.
    }
  }

  /** The live AudioContext, created on first use and resumed if the browser parked it. */
  private context(): AudioContext | null {
    try {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) {
        return null;
      }
      this.audioContext ||= new Ctor();
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => undefined);
      }
      return this.audioContext;
    } catch {
      return null;
    }
  }

  /**
   * The incoming-message chime: a rising C–E–G struck like a small bell.
   *
   * Synthesized rather than shipped as an asset, so there is no extra request and nothing to 404
   * in a stripped build. Each note is a sine fundamental plus a quieter partial an octave up —
   * that second voice is what stops it sounding like a test tone — shaped by a fast attack and a
   * long exponential tail, then rolled off through a lowpass so it reads as warm rather than
   * piercing on laptop speakers.
   *
   * `volume` scales the whole thing: the store plays it quietly for a thread you are already
   * looking at, where a full-strength chime would be nagging rather than informative.
   */
  ping(volume = 1): void {
    const ctx = this.context();
    if (!ctx) {
      return;
    }

    try {
      const now = ctx.currentTime;

      // One shared output stage, so the three notes ring together instead of stacking three
      // independent gains straight onto the speakers.
      const master = ctx.createGain();
      master.gain.setValueAtTime(Math.max(0, Math.min(1, volume)) * 0.5, now);

      const tone = ctx.createBiquadFilter();
      tone.type = 'lowpass';
      tone.frequency.setValueAtTime(3200, now);
      tone.Q.setValueAtTime(0.7, now);

      tone.connect(master);
      master.connect(ctx.destination);

      // C6, E6, G6 — a major triad, which resolves rather than merely stopping.
      const notes = [1046.5, 1318.51, 1567.98];
      notes.forEach((frequency, index) => {
        const at = now + index * 0.085;
        // The last note is the one left ringing; the first two are struck and released under it.
        const decay = index === notes.length - 1 ? 0.85 : 0.42;
        this.strike(ctx, tone, frequency, at, decay, 0.5);
        this.strike(ctx, tone, frequency * 2, at, decay * 0.6, 0.14);
      });
    } catch {
      // Audio is a nicety — never let it surface as an error.
    }
  }

  // ---- Ringing ---------------------------------------------------------------

  /**
   * Ring for an incoming call until something stops it.
   *
   * A call needs a different shape of sound from a message, not just a louder one: the chime is
   * over before anyone has looked up, which is right for a message and useless for a call. This
   * repeats until answered, ignored, or `timeoutMs` runs out — a ring nobody stops is a ring
   * that has already failed, and one that goes on forever is worse than none.
   */
  startRing(timeoutMs = 30000): void {
    this.stopRing();
    this.ringBurst();
    this.ringInterval = setInterval(() => this.ringBurst(), ChatNotificationService.RING_PERIOD_MS);
    this.ringTimeout = setTimeout(() => this.stopRing(), timeoutMs);
  }

  /** Silence the ring and dismiss its notification. Safe to call when nothing is ringing. */
  stopRing(): void {
    if (this.ringInterval) {
      clearInterval(this.ringInterval);
      this.ringInterval = null;
    }
    if (this.ringTimeout) {
      clearTimeout(this.ringTimeout);
      this.ringTimeout = null;
    }
    try {
      this.ringNotification?.close();
    } catch {
      // A notification the browser already tidied up throws on close; there is nothing to do.
    }
    this.ringNotification = null;
  }

  /**
   * The sticky desktop notification for a call.
   *
   * `requireInteraction` keeps it on screen instead of fading after a few seconds — the whole
   * point is that it is still there when the user comes back to their machine. Held on the
   * instance so {@link stopRing} can take it down the moment the call is answered elsewhere.
   */
  notifyCall(callerName: string, conversationLabel: string, onClick: () => void): void {
    if (!this.granted || document.visibilityState === 'visible') {
      // A visible tab gets the in-app banner, which has Join and Ignore on it.
      return;
    }
    try {
      const notification = new Notification(`📹 ${callerName} is calling`, {
        body: conversationLabel ? `Video call in ${conversationLabel}` : 'Video call',
        icon: 'assets/img/logo/favicon.png',
        tag: 'remmi-chat-call',
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
        onClick();
      };
      this.ringNotification = notification;
    } catch {
      // Never let a notification the browser refused break the message path.
    }
  }

  /**
   * One burst: two rising strikes, twice, which is the cadence a phone uses and the reason this
   * reads as "answer me" rather than as a longer version of the message chime.
   */
  private ringBurst(): void {
    const ctx = this.context();
    if (!ctx) {
      return;
    }

    try {
      const now = ctx.currentTime;

      const master = ctx.createGain();
      master.gain.setValueAtTime(0.45, now);

      const tone = ctx.createBiquadFilter();
      tone.type = 'lowpass';
      tone.frequency.setValueAtTime(2600, now);
      tone.Q.setValueAtTime(0.7, now);

      tone.connect(master);
      master.connect(ctx.destination);

      // A5 then C#6, struck twice — a major third, so it carries without sounding like an alarm.
      [0, 0.44].forEach(offset => {
        const at = now + offset;
        this.strike(ctx, tone, 880, at, 0.3, 0.5);
        this.strike(ctx, tone, 1108.73, at + 0.15, 0.3, 0.45);
      });
    } catch {
      // Audio is a nicety — never let it surface as an error.
    }
  }

  /** Long enough that the two bursts read as one ring with a gap after it, not as four beeps. */
  private static readonly RING_PERIOD_MS = 2600;

  private ringInterval: ReturnType<typeof setInterval> | null = null;
  private ringTimeout: ReturnType<typeof setTimeout> | null = null;
  private ringNotification: Notification | null = null;

  /** One struck partial: instant attack, exponential tail, freed as soon as it has rung out. */
  private strike(ctx: AudioContext, destination: AudioNode, frequency: number, at: number, decay: number, peak: number): void {
    const gain = ctx.createGain();
    // Exponential ramps cannot touch zero, hence the near-silent floor either side.
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(peak, at + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    gain.connect(destination);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, at);
    osc.connect(gain);
    osc.start(at);
    osc.stop(at + decay + 0.05);
    osc.onended = () => gain.disconnect();
  }
}
